/**
 * Product card header — the top strip of every product card showing:
 *   • Brand / Series (with per-category layout variations)
 *   • Product code badge (with veneer prefix handling)
 *   • Hardware System badges (load capacity, color, feature, door type)
 *   • Category badge on the right
 *
 * Extracted from ProductsPage.js. Pure JSX — only
 * dependency is the `group` object built by `groupProducts`.
 *
 * Deliberately preserves the original 3-way branching for the heading
 * layout (thickness-bulk vs hardware-system vs default) rather than
 * "cleaning it up" — each branch matches a specific catalog category's
 * label conventions that HEDA's sales team already knows by muscle
 * memory. Any consolidation risks changing what staff see on the
 * cards.
 */
import React from 'react';
import { Badge } from '../../../components/ui/badge';


export function ProductCardHeader({ group }) {
  return (
    <div className="flex justify-between items-start mb-2">
      <div className="flex-1 min-w-0">
        {/* Thickness/Weight bulk categories: Brand - Subcategory on same line, Grade below */}
        {(group.isThicknessBulk || group.isWeightBulk) ? (
          <>
            <h3 className="font-bold text-[#1A1C16] truncate">
              {group.brand} {group.name && `- ${group.name}`}
            </h3>
            {group.grade && (
              <p className="text-sm text-[#5C6150]">{group.grade}</p>
            )}
          </>
        ) : group.isHardwareSystem ? (
          <>
            {/* Hardware: Brand - Series on same line */}
            <h3 className="font-bold text-[#1A1C16]">
              {group.brand} - {group.name}
            </h3>
            {/* Hardware type badge on left, below brand name */}
            <div className="flex items-center gap-1.5 mt-1">
              <Badge variant="outline" className="text-xs border-[#556B2F] text-[#556B2F] capitalize">
                {group.hardware_system_type?.replace(/_/g, ' ')}
              </Badge>
            </div>
          </>
        ) : (
          <>
            {group.brand ? (
              <>
                <h3 className="font-bold text-[#1A1C16]">{group.brand}</h3>
                <p className="text-sm text-[#5C6150]">{group.name}</p>
              </>
            ) : (
              // No brand (e.g. Lipping Patti) — show product type as the heading
              <h3 className="font-bold text-[#1A1C16]">{group.name}</h3>
            )}
          </>
        )}
      </div>
      {/* Badges: Specs + Category on right */}
      <div className="flex flex-col gap-1 flex-shrink-0 ml-2 items-end">
        {/* Spec badges row */}
        <div className="flex gap-1 flex-wrap justify-end">
          {/* Product Code badge - show for all categories that have it */}
          {group.product_code && (
            <Badge variant="outline" className="text-xs border-[#DAA520] text-[#8B6914] font-mono font-bold">
              {/* For Veneers, show full code with prefix */}
              {group.isVeneer && group.code_prefix
                ? `${group.code_prefix}-${group.product_code}`
                : group.product_code}
            </Badge>
          )}
          {/* Load Capacity for Tandem/Pullout */}
          {group.isHardwareSystem && group.load_capacity && (
            <Badge className="text-xs bg-[#DAA520] text-white font-bold">
              {group.load_capacity}
            </Badge>
          )}
          {/* Color/Finish - olive theme */}
          {group.isHardwareSystem && group.color && (
            <Badge variant="outline" className="text-xs border-[#556B2F] text-[#556B2F]">
              {group.color}
            </Badge>
          )}
          {/* Feature (Soft Close, AUTO HINGES, etc.) - olive theme */}
          {group.isHardwareSystem && group.feature && (
            <Badge className="text-xs bg-[#556B2F] text-white">
              {group.feature}
            </Badge>
          )}
          {/* Door Type for Sliding - olive theme */}
          {group.isHardwareSystem && group.hardware_system_type === 'sliding' && group.door_type && (
            <Badge className="text-xs bg-[#556B2F] text-white">
              {group.door_type}
            </Badge>
          )}
        </div>
        {/* Category badge */}
        <Badge variant="outline" className="text-xs">{group.category}</Badge>
      </div>
    </div>
  );
}
